import React, { useEffect, useState } from 'react';
import { SkippableFragment } from '@funtech-int/kp-graphql-sdk/yango-movies-www';

import { useIsPlayerStarted } from '@/scenarios/legacy-player/hooks/useIsPlayerStarted';
import { useOnSeekingFilmoscope } from '@/scenarios/legacy-player/hooks/filmoscope/useOnSeekingFilmoscope';
import { usePlayingState } from '@/scenarios/legacy-player/hooks/usePlayingState';

import { usePrerollStageWatcher } from '../../../hooks/skin/use-preroll-stage-watcher';
import { useCurrentSkippableFragment } from '../../../hooks/skin/use-current-skippable-fragment';
import { usePrerollDuration } from '../../../hooks/skin/use-preroll-duration';
import { SkinStage } from '../../../hooks/skin/use-skin-stage';

import { MoviePrerollView } from './component';

const PREROLL_DURATION_PADDING = 3;

export interface MoviePrerollViewContainerProps {
    isPlannedToWatch: boolean;
    skippableFragments: SkippableFragment[];
    movieId: number;
    className: string;
    onStart: () => void;
    onClose: () => void;
    checkStage: (stage: SkinStage) => boolean;
    onBookmarkClick: () => void;
}

const usePrerollSkipAnimationController = () => {
    const [isAnimated, setIsAnimated] = useState(false);

    const [isSeeked, setIsSeeked] = useState(false);
    const [isPaused, setIsPaused] = useState(false);

    useOnSeekingFilmoscope(() => setIsSeeked(true));

    const isPausedState = usePlayingState().playingState === 'pause';

    useEffect(() => {
        if (isPausedState) {
            setIsPaused(true);
        }
    }, [isPausedState]);

    useEffect(() => {
        if (!isSeeked && !isPaused) {
            setIsAnimated(true);
        } else {
            setIsAnimated(false);
        }
    }, [isAnimated, isPaused, isSeeked]);

    return isAnimated;
};

export const MoviePrerollViewContainer = ({
    className,
    skippableFragments,
    onStart,
    onClose,
    checkStage,
    movieId,
    isPlannedToWatch,
    onBookmarkClick,
}: MoviePrerollViewContainerProps) => {
    const currentFragment = useCurrentSkippableFragment(skippableFragments);

    const prerollDuration = usePrerollDuration();
    usePrerollStageWatcher({ onStart, onClose });

    const isPlayerStarted = useIsPlayerStarted();

    const isAnimated = usePrerollSkipAnimationController();

    if (isPlayerStarted && checkStage('preroll') && currentFragment?.type === 'PREROLL') {
        return (
            <MoviePrerollView
                prerollDuration={(prerollDuration ?? 0) + PREROLL_DURATION_PADDING}
                className={className}
                currentFragment={currentFragment}
                isInFavorites={isPlannedToWatch}
                movieId={movieId}
                isAnimated={isAnimated}
                onBookmarkClick={onBookmarkClick}
            />
        );
    }

    return null;
};
